/**
 * O parser da guia DAE do eSocial doméstico em PDF.
 *
 * Papel: aqui ficam `lerGuiaDae`, que reproduz `parseGuiaDaePdfTexto`
 * (l. 4407–4443), e `CODIGO_DAE_SUBCATEGORIA`, os sete códigos da guia que têm
 * subcategoria própria em "Despesas com Pessoal" (l. 4397–4405).
 *
 * Como usar: a guia que chega aqui é a das duas importações — a de Lançamentos,
 * que vira lançamentos, e a do cartão de Outros Bancos, que só informa. O código
 * fora dos sete grava a própria denominação como subcategoria, e o plano de
 * contas a ganha na gravação.
 *
 * O vencimento é a **primeira** data do texto, mesmo quando outra data aparece
 * antes do vencimento de verdade: é a regra da origem, aprovada como está, e a
 * conferência deixa a data editável.
 */
import { MESES_POR_EXTENSO } from '@concierge/shared';

import { numeroDoPdf } from './folha-pdf';

/** A subcategoria de cada código da guia; os demais usam a denominação. */
export const CODIGO_DAE_SUBCATEGORIA: Readonly<Record<string, string>> = {
  '1082': 'INSS',
  '1138': 'INSS',
  '1646': 'RAT',
  '0561': 'IRRF',
  '1251': 'FGTS Compensatório',
  '1718': 'FGTS',
  '1606': 'Repasse Empréstimo Consignado do Trabalhador',
};

/** Uma linha da guia. */
export interface ItemDaGuia {
  codigo: string;
  denominacao: string;
  subcategoria: string;
  valor: number;
}

/** O que `lerGuiaDae` devolve. */
export interface GuiaDoPdf {
  itens: ItemDaGuia[];
  /** O período de apuração em `AAAA-MM`, ou `null`. */
  competencia: string | null;
  competenciaDetectada: boolean;
  /** A primeira data do texto, em `AAAA-MM-DD`, ou `null`. */
  vencimento: string | null;
  /** O primeiro número depois de "Totais", ou `null`. */
  totalPdf: number | null;
}

/** Um número monetário em formato brasileiro. */
const NUMERO_MONETARIO = /\d{1,3}(?:\.\d{3})*,\d{2}/g;

/** O período de apuração escrito como `Setembro/2026`. */
const PERIODO = new RegExp(`(${MESES_POR_EXTENSO.join('|')})\\s*\\/\\s*(20\\d{2})`, 'i');

/**
 * Lê as linhas, o período, o vencimento e o total de uma guia DAE.
 *
 * Cada linha que começa por quatro dígitos é um item: a denominação é o resto
 * da linha, e o valor é o **último** número da linha seguinte (o total da linha:
 * principal, multa e juros). A linha de código sem número na seguinte fica de
 * fora.
 *
 * @param texto - O texto do PDF, de `extrairTextoDoPdf`.
 * @returns Os itens, o período em `AAAA-MM`, o vencimento em ISO e o total
 *   impresso, que é o primeiro número nos 120 caracteres depois de "Totais".
 */
export function lerGuiaDae(texto: string): GuiaDoPdf {
  const linhas = texto.split('\n');
  const itens: ItemDaGuia[] = [];
  linhas.forEach((linha, indice) => {
    const casamento = /^(\d{4})\s+(.+?)\s*$/.exec(linha);
    if (casamento === null) return;
    const valor = (linhas[indice + 1] ?? '').match(NUMERO_MONETARIO)?.at(-1);
    if (valor === undefined) return;
    const codigo = casamento[1] ?? '';
    const denominacao = (casamento[2] ?? '').replace(/\s+/g, ' ').trim();
    itens.push({
      codigo,
      denominacao,
      subcategoria: CODIGO_DAE_SUBCATEGORIA[codigo] ?? denominacao,
      valor: numeroDoPdf(valor),
    });
  });

  const periodo = PERIODO.exec(texto);
  const mes =
    periodo === null
      ? -1
      : MESES_POR_EXTENSO.findIndex(
          (nome) => nome.toLowerCase() === periodo[1]?.toLowerCase(),
        );
  const competencia =
    periodo === null || mes === -1
      ? null
      : `${periodo[2] ?? ''}-${String(mes + 1).padStart(2, '0')}`;

  const data = /(\d{2})\/(\d{2})\/(\d{4})/.exec(texto);
  const posicaoDosTotais = texto.indexOf('Totais');
  const total =
    posicaoDosTotais === -1
      ? undefined
      : texto.slice(posicaoDosTotais, posicaoDosTotais + 120).match(NUMERO_MONETARIO)?.[0];

  return {
    itens,
    competencia,
    competenciaDetectada: competencia !== null,
    vencimento: data === null ? null : `${data[3] ?? ''}-${data[2] ?? ''}-${data[1] ?? ''}`,
    totalPdf: total === undefined ? null : numeroDoPdf(total),
  };
}
